import { useAtomValue, useSetAtom, useStore } from "jotai";
import { type JSX, useEffect, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { ClientContactDialog } from "@frontend/features/clients";
import { useArrayField } from "@frontend/hooks/form";
import type { ArrayItemField, NumberField } from "@frontend/lib/form";
import {
  EmployeeField,
  employeesAtom,
  employeesLoadedAtom,
  loadEmployeesAtom,
} from "@frontend/shared/employee-field";
import { InterlocutorDialog } from "@frontend/shared/interlocutor-dialog";
import type { InterlocutorValues } from "@frontend/shared/interlocutor-dialog";
import {
  PeopleGroup,
  PeopleGroups,
  PersonCard,
} from "@frontend/shared/people-group";
import { useLingui } from "@lingui/react/macro";

import {
  clientScopeAtom,
  optionsAtom,
  projectEditorAtom,
  projectForm,
  reloadClientContactsAtom,
  scopeLoadingAtom,
} from "../stores/editor";
import { SectionFrame } from "./Section";

const fields = projectForm.fields;

type ContactItem = ArrayItemField<typeof fields.clientContacts>;

type AccountantItem = ArrayItemField<typeof fields.accountants>;

type ReferenceAtom = NumberField<number | null | undefined>;

type Editing = {
  group: "contacts" | "accountants";
  index: number | null;
  values: InterlocutorValues;
  fallbackLabel: string | null;
};

const empty: InterlocutorValues = { personId: null, typeId: null };

export function Interlocutors(): JSX.Element {
  const { t } = useLingui();

  const store = useStore();

  const contacts = useArrayField(fields.clientContacts);
  const accountants = useArrayField(fields.accountants);

  const options = useAtomValue(optionsAtom);
  const scope = useAtomValue(clientScopeAtom);
  const employees = useAtomValue(employeesAtom);
  const employeesLoaded = useAtomValue(employeesLoadedAtom);
  const scopeLoading = useAtomValue(scopeLoadingAtom);
  const clientId = useAtomValue(fields.clientId.atom);

  const loadEmployees = useSetAtom(loadEmployeesAtom);
  const reloadContacts = useSetAtom(reloadClientContactsAtom);

  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const originRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const edit = (next: Editing, origin: DOMRect): void => {
    originRef.current = origin;

    setEditing(next);
    setEditorOpen(true);
  };

  const commit = (values: InterlocutorValues): void => {
    if (null === editing) {
      return;
    }

    const { group, index } = editing;

    if ("contacts" === group) {
      if (null === index) {
        contacts.append({
          id: null,
          contactId: values.personId,
          typeId: values.typeId,
        });

        return;
      }

      const item = contacts.items[index];

      write(store, item?.fields.contactId, values.personId);
      write(store, item?.fields.typeId, values.typeId);

      return;
    }

    if (null === index) {
      accountants.append({
        id: null,
        employeeId: values.personId,
        typeId: values.typeId,
      });

      return;
    }

    const item = accountants.items[index];

    write(store, item?.fields.employeeId, values.personId);
    write(store, item?.fields.typeId, values.typeId);
  };

  const adding = null === editing?.index;
  const forContacts = "accountants" !== editing?.group;

  return (
    <SectionFrame icon="Users" legend={t`Interlocutors`}>
      <PeopleGroups>
        <PeopleGroup
          title={t`Client contacts`}
          count={contacts.count}
          empty={t`No contact named on this project.`}
          addLabel={t`Add a contact`}
          loading={scopeLoading}
          addDisabled={0 === scope.contacts.length}
          onAdd={(origin) =>
            edit(
              {
                group: "contacts",
                index: null,
                values: empty,
                fallbackLabel: null,
              },
              origin,
            )
          }
          action={
            <Button
              variant="link"
              size="sm"
              disabled={null == clientId}
              leftSection={<Icon name="UserPlus" size="sm" />}
              onClick={() => setPicking(true)}
            >
              {t`New contact for this client`}
            </Button>
          }
        >
          {contacts.items.map((contact, index) => (
            <ClientContactCard
              key={contact.path}
              item={contact}
              onEdit={(values, fallbackLabel, origin) =>
                edit(
                  { group: "contacts", index, values, fallbackLabel },
                  origin,
                )
              }
              onRemove={() => contacts.remove(contacts.itemAtoms[index]!)}
            />
          ))}
        </PeopleGroup>

        <PeopleGroup
          title={t`In charge`}
          count={accountants.count}
          empty={t`Nobody is in charge of this project yet.`}
          addLabel={t`Add someone`}
          loading={!employeesLoaded}
          addDisabled={0 === employees.length}
          onAdd={(origin) =>
            edit(
              {
                group: "accountants",
                index: null,
                values: empty,
                fallbackLabel: null,
              },
              origin,
            )
          }
        >
          {accountants.items.map((accountant, index) => (
            <AccountantCard
              key={accountant.path}
              item={accountant}
              onEdit={(values, fallbackLabel, origin) =>
                edit(
                  { group: "accountants", index, values, fallbackLabel },
                  origin,
                )
              }
              onRemove={() => accountants.remove(accountants.itemAtoms[index]!)}
            />
          ))}
        </PeopleGroup>
      </PeopleGroups>

      <ClientContactDialog
        open={picking}
        onOpenChange={setPicking}
        clientId={clientId ?? null}
        onCreated={(created) => {
          if (null == clientId) {
            return;
          }

          void reloadContacts(clientId).then(() =>
            contacts.append({
              id: null,
              contactId: created.id,
              typeId: created.typeId,
            }),
          );
        }}
      />

      <InterlocutorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        originRef={originRef}
        icon={adding ? "UserPlus" : "UserPen"}
        title={
          forContacts
            ? adding
              ? t`Add a contact`
              : t`Change the contact`
            : adding
              ? t`Add someone in charge`
              : t`Change who is in charge`
        }
        personLabel={forContacts ? t`Contact` : t`Employee`}
        people={forContacts ? scope.contacts : undefined}
        personControl={
          forContacts
            ? undefined
            : (field) => (
                <EmployeeField
                  field={field}
                  fallbackLabel={editing?.fallbackLabel}
                />
              )
        }
        parts={
          forContacts ? options.clientContactTypes : options.accountantTypes
        }
        values={editing?.values ?? empty}
        fallbackLabel={editing?.fallbackLabel}
        onSubmit={commit}
      />
    </SectionFrame>
  );
}

function write(
  store: ReturnType<typeof useStore>,
  field: ReferenceAtom | undefined,
  value: number | null,
): void {
  if (undefined === field) {
    return;
  }

  store.set(field.atom, value);
  store.set(field.touch);
}

type CardProps<TItem> = {
  item: TItem;
  onEdit: (
    values: InterlocutorValues,
    fallbackLabel: string | null,
    origin: DOMRect,
  ) => void;
  onRemove: () => void;
};

function ClientContactCard({
  item,
  onEdit,
  onRemove,
}: CardProps<ContactItem>): JSX.Element {
  const options = useAtomValue(optionsAtom);
  const scope = useAtomValue(clientScopeAtom);
  const { project } = useAtomValue(projectEditorAtom);

  const contactId = useAtomValue(item.fields.contactId.atom);
  const typeId = useAtomValue(item.fields.typeId.atom);

  const known = project?.clientContacts.find(
    (contact) => contact.contactId === contactId,
  );

  const option = scope.contacts.find(({ id }) => id === contactId);

  return (
    <PersonCard
      name={option?.label ?? known?.contactLabel ?? null}
      hint={option?.hint ?? null}
      avatarUrl={option?.avatarUrl ?? known?.avatarUrl ?? null}
      role={
        options.clientContactTypes.find(({ id }) => id === typeId)?.label ??
        known?.typeLabel ??
        null
      }
      onEdit={(origin) =>
        onEdit(
          { personId: contactId ?? null, typeId: typeId ?? null },
          known?.contactLabel ?? null,
          origin,
        )
      }
      onRemove={onRemove}
    />
  );
}

function AccountantCard({
  item,
  onEdit,
  onRemove,
}: CardProps<AccountantItem>): JSX.Element {
  const options = useAtomValue(optionsAtom);
  const employees = useAtomValue(employeesAtom);
  const { project } = useAtomValue(projectEditorAtom);

  const employeeId = useAtomValue(item.fields.employeeId.atom);
  const typeId = useAtomValue(item.fields.typeId.atom);

  const known = project?.accountants.find(
    (accountant) => accountant.employeeId === employeeId,
  );

  const option = employees.find(({ id }) => id === employeeId);

  return (
    <PersonCard
      name={option?.label ?? known?.employeeLabel ?? null}
      hint={option?.department ?? null}
      avatarUrl={option?.avatarUrl ?? null}
      role={
        options.accountantTypes.find(({ id }) => id === typeId)?.label ??
        known?.typeLabel ??
        null
      }
      onEdit={(origin) =>
        onEdit(
          { personId: employeeId ?? null, typeId: typeId ?? null },
          known?.employeeLabel ?? null,
          origin,
        )
      }
      onRemove={onRemove}
    />
  );
}
